import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Button } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import SweetAlert from "react-bootstrap-sweetalert";
import { CircularProgress } from "@material-ui/core";
import moment from "moment";

const BASE_URL = process.env.REACT_APP_TOKRI_API_URL;

const ToolsPage = () => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastClearedLabel, setLastClearedLabel] = useState("N/A");
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  const getAuthHeaders = useCallback((authToken) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");
    myHeaders.append("Authorization", `Bearer ${authToken}`);
    return myHeaders;
  }, []);

  const formatClearedAt = useCallback((clearedAt) => {
    if (!clearedAt) return "N/A";
    const formatted = moment(clearedAt, "YYYY-MM-DD HH:mm:ss", true).format(
      "DD/MM/YYYY hh:mm a"
    );
    return formatted === "Invalid date" ? "N/A" : formatted;
  }, []);

  const getLastCacheClear = useCallback(async (authToken) => {
    if (!authToken) {
      setLastClearedLabel("N/A");
      return;
    }

    try {
      const requestOptions = {
        method: "GET",
        headers: getAuthHeaders(authToken),
        redirect: "follow",
      };

      const response = await fetch(
        `${BASE_URL}/api/cloudflare/last-cache-clear`,
        requestOptions
      );

      if (!response.ok) {
        setLastClearedLabel("N/A");
        return;
      }
      const result = await response.json();
      if (result?.message === "No cache clear record found.") {
        setLastClearedLabel("N/A");
        return;
      }
      setLastClearedLabel(formatClearedAt(result?.cleared_at));
    } catch (error) {
      console.log("Error fetching last cache clear:", error);
      setLastClearedLabel("N/A");
    }
  }, [formatClearedAt, getAuthHeaders]);

  useEffect(() => {
    const storedToken = localStorage.getItem("AuthenticationToken") || "";
    setToken(storedToken);
    getLastCacheClear(storedToken);
  }, [getLastCacheClear]);

  const clearCloudflareCache = async () => {
    if (loading || !token) return;

    setLoading(true);
    setErrorAlert(false);

    try {
      const formdata = new FormData();
      const requestOptions = {
        method: "POST",
        headers: getAuthHeaders(token),
        body: formdata,
        redirect: "follow",
      };

      const response = await fetch(
        `${BASE_URL}/api/cloudflare/clear-cache`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }
      const result = await response.json();
      setLastClearedLabel(formatClearedAt(result?.cleared_at));

      setSuccessAlert(true);
    } catch (error) {
      console.log("Error clearing cache:", error);
      setErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs breadcrumbItem="Tools" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={4}>
                      <Button
                        color="success"
                        onClick={clearCloudflareCache}
                        disabled={loading}
                        style={{ width: "180px" }}
                      >
                        {loading ? (
                          <CircularProgress size={15} value={100} color={"white"} />
                        ) : (
                          "Clear Cloudflare Cache"
                        )}
                      </Button>
                    </Col>
                    <Col md={8}>
                      <p className="mb-0">
                        Last cleared: {lastClearedLabel}
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
          {successAlert && (
            <SweetAlert
              title="Success!"
              success
              confirmBtnBsStyle="success"
              onConfirm={() => setSuccessAlert(false)}
            >
              {"Cloudflare cache has been cleared successfully."}
            </SweetAlert>
          )}
          {errorAlert && (
            <SweetAlert
              title="Error!"
              danger
              confirmBtnBsStyle="danger"
              onConfirm={() => setErrorAlert(false)}
            >
              {"Unable to clear Cloudflare cache. Please try again."}
            </SweetAlert>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ToolsPage;
